/* eslint-disable @typescript-eslint/no-explicit-any */
/* eslint-disable @typescript-eslint/no-unused-vars */
import type { 
  MarketSize, 
  MarketFactors, 
  MarketSegment, 
  TechnologyAdoption,
  MarketResponse,
  EconomicCycle,
  TechnologyDisruption,
  MarketEvent,
  RegionalMarket,
  MarketConsolidation
} from '@/types/tycoon';
import type { Product } from '@/types/product';
import { GAME_CONSTANTS } from '../../config/gameConstants';
import { GAME_CONSTANTS as LOCAL_GAME_CONSTANTS } from '@/config/gameConstants';

export class MarketSimulatorService {
  private static instance: MarketSimulatorService;
  private marketSize: MarketSize;
  private economicCycle: EconomicCycle;
  private technologyAdoptions: Map<string, TechnologyAdoption> = new Map();
  private regionalMarkets: Map<string, RegionalMarket> = new Map();
  private marketConsolidation: MarketConsolidation;
  private activeEvents: MarketEvent[] = [];

  private constructor() {
    this.marketSize = this.initializeMarketSize();
    this.economicCycle = this.initializeEconomicCycle();
    this.marketConsolidation = this.initializeMarketConsolidation();
    this.initializeRegionalMarkets();
    this.initializeTechnologyAdoptions();
  }

  public static getInstance(): MarketSimulatorService {
    if (!MarketSimulatorService.instance) {
      MarketSimulatorService.instance = new MarketSimulatorService();
    }
    return MarketSimulatorService.instance;
  }

  // Phase 1: Core Dynamic Market Size System
  public calculateDynamicMarketSize(timeElapsed: number): MarketSize {
    const factors = this.calculateMarketFactors(timeElapsed);
    const segments = this.updateMarketSegments(factors, timeElapsed);
    
    const baseMarket = GAME_CONSTANTS.market.initialSize; // Base TAM from config
    const totalAddressableMarket = baseMarket * factors.economic * factors.technology;
    
    // Calculate available market based on adoption and penetration
    const adoptionMultiplier = this.calculateGlobalAdoptionRate();
    const availableMarket = totalAddressableMarket * adoptionMultiplier * factors.regulatory;
    
    // Calculate current penetration across all segments
    const currentPenetration = segments.reduce((total, segment) => {
      return total + (segment.size * this.getSegmentPenetration(segment.id));
    }, 0) / totalAddressableMarket;

    // Calculate growth rate based on technology adoption and economic factors
    const baseGrowthRate = GAME_CONSTANTS.market.baseGrowthRate;
    const growthRate = baseGrowthRate * factors.economic * factors.technology * factors.social;

    this.marketSize = {
      totalAddressableMarket,
      availableMarket,
      currentPenetration: Math.min(currentPenetration, 1),
      growthRate: Math.max(growthRate, GAME_CONSTANTS.market.maxNegativeGrowth), // Cap negative growth
      segments,
      factors,
      timestamp: Date.now()
    };

    return this.marketSize;
  }

  private calculateMarketFactors(timeElapsed: number): MarketFactors {
    const economicFactor = this.calculateEconomicFactor(timeElapsed);
    const seasonalFactor = this.calculateSeasonalFactor();
    const technologyFactor = this.calculateTechnologyFactor();
    const competitionFactor = this.calculateCompetitionFactor();
    const regulatoryFactor = this.calculateRegulatoryFactor();
    const socialFactor = this.calculateSocialFactor(timeElapsed);

    return {
      economic: economicFactor,
      seasonal: seasonalFactor,
      technology: technologyFactor,
      competition: competitionFactor,
      regulatory: regulatoryFactor,
      social: socialFactor
    };
  }

  private calculateEconomicFactor(timeElapsed: number): number {
    // Update economic cycle
    this.updateEconomicCycle(timeElapsed);
    
    switch (this.economicCycle.phase) {
      case 'recession':
        return 0.5 + (this.economicCycle.consumerConfidence / 100) * 0.3;
      case 'recovery':
        return 0.8 + (this.economicCycle.gdpGrowth / 10) * 0.3;
      case 'expansion':
        return 1.2 + (this.economicCycle.gdpGrowth / 10) * 0.2;
      case 'peak':
        return 1.4 + (this.economicCycle.consumerConfidence / 100) * 0.1;
      case 'contraction':
        return 0.9 - (this.economicCycle.inflationRate / 10) * 0.2;
      default:
        return 1.0;
    }
  }

  private calculateSeasonalFactor(): number {
    const month = new Date().getMonth();
    // Q4 (Oct-Dec) typically strongest for consumer electronics
    // Q1 (Jan-Mar) typically weakest
    const seasonalMultipliers = [
      0.85, 0.80, 0.90, // Q1: Jan-Mar (post-holiday slump)
      0.95, 1.00, 1.05, // Q2: Apr-Jun (steady growth)
      1.10, 1.05, 0.95, // Q3: Jul-Sep (back-to-school)
      1.20, 1.25, 1.30  // Q4: Oct-Dec (holiday season)
    ];
    
    return seasonalMultipliers[month];
  }

  private calculateTechnologyFactor(): number {
    let factor = 1.0;
    
    // Aggregate impact of all active technology adoptions
    this.technologyAdoptions.forEach(adoption => {
      if (adoption.stage === 'early_majority' || adoption.stage === 'late_majority') {
        factor *= (1 + adoption.marketImpact * adoption.adoptionRate);
      }
    });

    // Apply disruption events
    this.activeEvents
      .filter(event => event.type === 'technology')
      .forEach(event => {
        factor *= (1 + event.effects.marketSize);
      });

    return Math.max(0.5, Math.min(factor, 3.0)); // Cap between 0.5x and 3x
  }

  private calculateCompetitionFactor(): number {
    const baseCompetition = 1.0;
    const marketConcentration = this.marketConsolidation.marketShareConcentration;
    
    // More consolidated markets have less growth potential
    const consolidationEffect = 1 - (marketConcentration * 0.3);
    
    // High barriers to entry reduce market dynamism
    const barrierEffect = 1 - (this.marketConsolidation.barrierToEntry * 0.2);
    
    return baseCompetition * consolidationEffect * barrierEffect;
  }

  private calculateRegulatoryFactor(): number {
    let factor = 1.0;
    
    // Apply regulatory events
    this.activeEvents
      .filter(event => event.type === 'regulatory')
      .forEach(event => {
        factor *= (1 + event.effects.marketSize);
      });
    
    return Math.max(0.6, Math.min(factor, 1.4)); // Cap between 0.6x and 1.4x
  }

  private calculateSocialFactor(timeElapsed: number): number {
    let factor = 1.0;
    
    // Apply social trend events
    this.activeEvents
      .filter(event => event.type === 'social')
      .forEach(event => {
        factor *= (1 + event.effects.marketSize);
      });
    
    // Long-term social trends (gradually increasing smartphone adoption)
    const yearlyTrend = 1 + (timeElapsed / (365 * 24 * 60 * 60 * 1000)) * 0.02; // 2% yearly increase
    
    return factor * yearlyTrend;
  }

  private updateMarketSegments(factors: MarketFactors, timeElapsed: number): MarketSegment[] {
    // Get current year for market evolution
    const currentYear = new Date().getFullYear();
    
    return this.marketSize.segments.map(segment => {
      // Apply year-based market evolution
      const evolution = this.getMarketEvolutionMultiplier(segment.name, currentYear);
      
      // Apply segment-specific growth
      const segmentGrowth = segment.growthRate * factors.economic * factors.social;
      const baseSize = this.getBaseSegmentSize(segment.name);
      const evolvedSize = baseSize * evolution;
      const newSize = evolvedSize * (1 + segmentGrowth * (timeElapsed / (365 * 24 * 60 * 60 * 1000)));
      
      // Adjust demographics based on market trends
      const updatedSegment: MarketSegment = {
        ...segment,
        size: Math.max(newSize, evolvedSize * 0.8), // Prevent more than 20% shrinkage from evolved size
        priceElasticity: this.adjustPriceElasticity(segment, factors),
        techSensitivity: this.adjustTechSensitivity(segment, factors, currentYear),
        brandLoyalty: this.adjustBrandLoyalty(segment, factors)
      };
      
      return updatedSegment;
    });
  }

  private adjustPriceElasticity(segment: MarketSegment, factors: MarketFactors): number {
    let elasticity = segment.priceElasticity;
    
    // Economic downturns increase price sensitivity
    if (factors.economic < 0.8) {
      elasticity *= 1.3;
    } else if (factors.economic > 1.2) {
      elasticity *= 0.8;
    }
    
    return Math.max(0.1, Math.min(elasticity, 2.0));
  }

  private adjustTechSensitivity(segment: MarketSegment, factors: MarketFactors, year: number): number {
    let sensitivity = segment.techSensitivity;
    
    // Technology booms increase tech sensitivity
    if (factors.technology > 1.5) {
      sensitivity *= 1.2;
    }
    
    // Year-based tech adoption increases sensitivity
    if (year >= 2010) {
      sensitivity *= 1.3; // Post-2010 consumers are more tech-aware
    } else if (year >= 2007) {
      sensitivity *= 1.1; // iPhone era begins
    }
    
    return Math.max(0.1, Math.min(sensitivity, 2.0));
  }

  private adjustBrandLoyalty(segment: MarketSegment, factors: MarketFactors): number {
    let loyalty = segment.brandLoyalty;
    
    // High competition reduces brand loyalty
    if (factors.competition < 0.8) {
      loyalty *= 0.9;
    }
    
    return Math.max(0.1, Math.min(loyalty, 1.0));
  }

  // Phase 1: Product Launch Market Response
  public calculateProductLaunchResponse(product: Product): MarketResponse {
    const launchDate = Date.now();
    const marketReception = this.calculateMarketReception(product);
    const competitorReactions = this.generateCompetitorReactions(product);
    const marketShareGain = this.calculateMarketShareGain(product, marketReception);
    const priceElasticityResponse = this.calculatePriceElasticityResponse(product);
    const adoptionCurve = this.generateAdoptionCurve(product, marketReception);

    return {
      productId: product.id,
      launchDate,
      marketReception,
      competitorReactions,
      marketShareGain,
      priceElasticityResponse,
      adoptionCurve
    };
  }

  private calculateMarketReception(product: Product): number {
    let reception = 0.5; // Base reception
    
    // Technology innovation impact
    const avgTechScore = product.techScore;
    reception += (avgTechScore - 50) / 100; // Normalize around 50
    
    // Price positioning impact
    const priceSegment = this.determinePriceSegment(product.price);
    const segmentReception = this.getSegmentReception(priceSegment);
    reception *= segmentReception;
    
    // Market timing impact
    const timingBonus = this.calculateTimingBonus(product);
    reception *= timingBonus;
    
    return Math.max(0.1, Math.min(reception, 1.0));
  }

  private generateCompetitorReactions(product: Product): any[] {
    // Will be implemented in CompetitorAIService
    return [];
  }

  private calculateMarketShareGain(product: Product, reception: number): number {
    const baseGain = reception * 0.1; // Max 10% initial market share gain
    const competitionFactor = this.marketSize.factors.competition;
    
    return baseGain * competitionFactor;
  }

  private calculatePriceElasticityResponse(product: Product): number {
    const priceSegment = this.determinePriceSegment(product.price);
    const segment = this.marketSize.segments.find(s => s.name.toLowerCase().includes(priceSegment));
    
    return segment?.priceElasticity || 1.0;
  }

  private generateAdoptionCurve(product: Product, reception: number): { week: number; penetration: number; salesVolume: number }[] {
    const curve = [];
    const maxPenetration = reception * 0.15; // Max 15% market penetration
    
    for (let week = 1; week <= 52; week++) {
      // S-curve adoption pattern
      const t = week / 52;
      const penetration = maxPenetration * (1 / (1 + Math.exp(-10 * (t - 0.5))));
      const salesVolume = this.calculateWeeklySales(penetration, week);
      
      curve.push({ week, penetration, salesVolume });
    }
    
    return curve;
  }

  // Helper methods
  private initializeMarketSize(): MarketSize {
    const segments: MarketSegment[] = [
      {
        id: 'budget',
        name: 'Budget Conscious',
        size: 150000000,
        priceElasticity: 1.8,
        techSensitivity: 0.6,
        brandLoyalty: 0.4,
        growthRate: 0.05,
        demographics: {
          ageRange: [18, 65],
          incomeRange: [20000, 50000],
          techAdoption: 'late'
        }
      },
      {
        id: 'mainstream',
        name: 'Mainstream Users',
        size: 250000000,
        priceElasticity: 1.2,
        techSensitivity: 0.8,
        brandLoyalty: 0.6,
        growthRate: 0.08,
        demographics: {
          ageRange: [25, 55],
          incomeRange: [40000, 80000],
          techAdoption: 'mainstream'
        }
      },
      {
        id: 'premium',
        name: 'Premium Enthusiasts',
        size: 80000000,
        priceElasticity: 0.8,
        techSensitivity: 1.5,
        brandLoyalty: 0.8,
        growthRate: 0.12,
        demographics: {
          ageRange: [20, 45],
          incomeRange: [60000, 150000],
          techAdoption: 'early'
        }
      },
      {
        id: 'enterprise',
        name: 'Enterprise Users',
        size: 50000000,
        priceElasticity: 0.6,
        techSensitivity: 1.0,
        brandLoyalty: 0.9,
        growthRate: 0.06,
        demographics: {
          ageRange: [25, 60],
          incomeRange: [50000, 200000],
          techAdoption: 'mainstream'
        }
      }
    ];

    return {
      totalAddressableMarket: 500000000,
      availableMarket: 400000000,
      currentPenetration: 0.6,
      growthRate: 0.08,
      segments,
      factors: {
        economic: 1.0,
        seasonal: 1.0,
        technology: 1.0,
        competition: 1.0,
        regulatory: 1.0,
        social: 1.0
      },
      timestamp: Date.now()
    };
  }

  private initializeEconomicCycle(): EconomicCycle {
    return {
      phase: 'expansion',
      duration: 84, // 7 years average cycle
      remainingMonths: 36,
      gdpGrowth: 2.5,
      consumerConfidence: 75,
      unemploymentRate: 5.2,
      inflationRate: 2.1,
      marketMultiplier: 1.1
    };
  }

  private initializeMarketConsolidation(): MarketConsolidation {
    return {
      trigger: 'market_maturity',
      phase: 'consolidated',
      competitorCount: 12,
      marketShareConcentration: 0.7,
      barrierToEntry: 0.8,
      acquisitionProbability: 0.15,
      newEntrantThreat: 0.3
    };
  }

  private initializeRegionalMarkets(): void {
    // Implementation for Phase 2
  }

  private initializeTechnologyAdoptions(): void {
    // Implementation for Phase 1
  }

  // Utility methods
  private calculateGlobalAdoptionRate(): number {
    return 0.8; // 80% of TAM is addressable
  }

  private getSegmentPenetration(segmentId: string): number {
    // Placeholder - will be calculated from actual market data
    return 0.6;
  }

  private updateEconomicCycle(timeElapsed: number): void {
    // Placeholder - will be fully implemented in Phase 3
  }

  private determinePriceSegment(price: number): string {
    if (price < 300) return 'budget';
    if (price < 700) return 'mainstream';
    if (price < 1200) return 'premium';
    return 'enterprise';
  }

  private getSegmentReception(priceSegment: string): number {
    const receptionMap: { [key: string]: number } = {
      budget: 0.8,
      mainstream: 1.0,
      premium: 1.2,
      enterprise: 0.9
    };
    return receptionMap[priceSegment] || 1.0;
  }

  private calculateTimingBonus(product: Product): number {
    // Placeholder for market timing analysis
    return 1.0;
  }

  private calculateWeeklySales(penetration: number, week: number): number {
    const baseVolume = this.marketSize.availableMarket * 0.1; // 10% weekly TAM
    return baseVolume * penetration;
  }

  // Public getters
  public getMarketSize(): MarketSize {
    return this.marketSize;
  }

  public getEconomicCycle(): EconomicCycle {
    return this.economicCycle;
  }

  public getTechnologyAdoptions(): Map<string, TechnologyAdoption> {
    return this.technologyAdoptions;
  }

  public getRegionalMarkets(): Map<string, RegionalMarket> {
    return this.regionalMarkets;
  }

  public getMarketConsolidation(): MarketConsolidation {
    return this.marketConsolidation;
  }

  public getActiveEvents(): MarketEvent[] {
    return this.activeEvents;
  }
  
  private getMarketEvolutionMultiplier(segmentName: string, year: number): number {
    const evolution = LOCAL_GAME_CONSTANTS.marketSegments.marketEvolution;
    const segmentKey = this.getSegmentKey(segmentName);
    let multiplier = 1.0;
    
    if (year <= evolution.featurePhoneEra.endYear) {
      multiplier = evolution.featurePhoneEra.segmentMultipliers[segmentKey] || 1.0;
    } else if (year <= evolution.smartphoneIntroduction.endYear) {
      // Interpolate between eras
      const progress = (year - evolution.featurePhoneEra.endYear) / 
                      (evolution.smartphoneIntroduction.endYear - evolution.featurePhoneEra.endYear);
      const start = evolution.featurePhoneEra.segmentMultipliers[segmentKey] || 1.0;
      const end = evolution.smartphoneIntroduction.segmentMultipliers[segmentKey] || 1.0;
      multiplier = start + (end - start) * progress;
    } else if (year <= evolution.mainstreamAdoption.endYear) {
      const progress = (year - evolution.smartphoneIntroduction.endYear) / 
                      (evolution.mainstreamAdoption.endYear - evolution.smartphoneIntroduction.endYear);
      const start = evolution.smartphoneIntroduction.segmentMultipliers[segmentKey] || 1.0;
      const end = evolution.mainstreamAdoption.segmentMultipliers[segmentKey] || 1.0;
      multiplier = start + (end - start) * progress;
    } else {
      multiplier = evolution.marketMaturity.segmentMultipliers[segmentKey] || 1.0;
    }
    
    return multiplier;
  }
  
  private getSegmentKey(segmentName: string): string {
    const normalized = segmentName.toLowerCase();
    if (normalized.includes('budget')) return 'budget';
    if (normalized.includes('mainstream')) return 'mainstream';
    if (normalized.includes('premium')) return 'premium';
    if (normalized.includes('enterprise')) return 'enterprise';
    return 'mainstream';
  }
  
  private getBaseSegmentSize(segmentName: string): number {
    const sizes = LOCAL_GAME_CONSTANTS.marketSegments.sizes;
    const key = this.getSegmentKey(segmentName);
    
    switch (key) {
      case 'budget': return sizes.budget;
      case 'mainstream': return sizes.mainstream;
      case 'premium': return sizes.premium;
      case 'enterprise': return sizes.enterprise;
      default: return sizes.mainstream;
    }
  }

  /**
   * Calculate market share distribution among competitors
   */
  public calculateMarketShare(
    competitors: Array<{ id: string; name: string; products: Array<{ id: string; techScore: number; price: number }> }>,
    segments: MarketSegment[],
    marketFactors: MarketFactors
  ): Array<{ companyId: string; companyName: string; share: number; trend: 'up' | 'down' | 'stable'; monthlyChange: number; products: number }> {
    const marketShareData: Array<{ companyId: string; companyName: string; share: number; trend: 'up' | 'down' | 'stable'; monthlyChange: number; products: number }> = [];
    
    // Calculate total market appeal for normalization
    let totalMarketAppeal = 0;
    const competitorAppeals = new Map<string, number>();
    
    competitors.forEach(competitor => {
      let appeal = 0;
      competitor.products.forEach(product => {
        // Calculate product appeal based on tech score and price
        const techAppeal = product.techScore / 100;
        const priceAppeal = 1 / (1 + Math.exp((product.price - 800) / 200)); // Sigmoid function centered at $800
        appeal += (techAppeal * 0.6 + priceAppeal * 0.4) * marketFactors.technology;
      });
      
      competitorAppeals.set(competitor.id, appeal);
      totalMarketAppeal += appeal;
    });
    
    // Calculate market share
    competitors.forEach(competitor => {
      const appeal = competitorAppeals.get(competitor.id) || 0;
      const share = totalMarketAppeal > 0 ? appeal / totalMarketAppeal : 0;
      
      marketShareData.push({
        companyId: competitor.id,
        companyName: competitor.name,
        share,
        trend: 'stable', // Would need historical data to calculate trend
        monthlyChange: 0, // Would need historical data
        products: competitor.products.length
      });
    });
    
    return marketShareData;
  }

  /**
   * Generate potential market events based on current conditions
   */
  public generateMarketEvents(
    economicCycle: EconomicCycle,
    segments: MarketSegment[],
    marketFactors: MarketFactors
  ): MarketEvent[] {
    const events: MarketEvent[] = [];
    
    // Economic cycle-based events
    if (economicCycle.phase === 'recession' && Math.random() < 0.3) {
      events.push({
        id: `recession-${Date.now()}`,
        type: 'economic',
        name: 'Economic Downturn Intensifies',
        description: 'Consumer spending decreases as recession deepens',
        probability: 0.8,
        triggers: [],
        effects: {
          marketSize: -0.1,
          segments: ['premium', 'enterprise'],
          competitors: [],
          duration: 6
        }
      });
    }
    
    // Technology disruption events
    if (marketFactors.technology > 1.2 && Math.random() < 0.2) {
      events.push({
        id: `tech-${Date.now()}`,
        type: 'technological',
        name: 'Technology Breakthrough',
        description: 'New technology creates market opportunities',
        probability: 0.6,
        triggers: [{ condition: 'technology', threshold: 1.2 }],
        effects: {
          marketSize: 0.05,
          segments: ['premium'],
          competitors: [],
          duration: 12
        }
      });
    }
    
    // Seasonal events
    const month = new Date().getMonth();
    if ((month === 10 || month === 11) && Math.random() < 0.4) {
      events.push({
        id: `holiday-${Date.now()}`,
        type: 'seasonal',
        name: 'Holiday Shopping Season',
        description: 'Increased consumer demand during holidays',
        probability: 0.9,
        triggers: [],
        effects: {
          marketSize: 0.15,
          segments: ['budget', 'mainstream'],
          competitors: [],
          duration: 2
        }
      });
    }
    
    return events;
  }
}

export const marketSimulator = MarketSimulatorService.getInstance();